/**
 * Path to First Payout Estimator
 * ================================
 * Calculates realistic estimated days from evaluation start to first payout.
 * Three phases: Eval → Funded Buffer → Payout Processing
 *
 * The estimate represents the fastest realistic path under each firm's rules.
 */

import { isPublicEvalType } from "@/utils/constants";

/** Extract first percentage from a consistency rule string */
export const parseConsistencyPct = (rule) => {
  if (!rule) return 0;
  const lower = rule.toLowerCase();
  if (lower.includes("none") || lower === "") return 0;
  // Match patterns like "30%", "40% consistency", "50% (eval)"
  const m = rule.match(/(\d+)%/);
  return m ? parseInt(m[1], 10) / 100 : 0;
};

/** Extract dollar amount from string like "$3,000" or "$2,500" */
const parseDollarVal = (s) => {
  if (!s) return 0;
  const m = String(s).match(/\$?([\d,]+)/);
  return m ? parseInt(m[1].replace(/,/g, ""), 10) : 0;
};

/** Parse funded min days from varied text formats */
const parseFundedMinDays = (val) => {
  if (!val) return 0;
  const s = String(val);
  // "20 days" → 20, "8 ATDs" → 8, "6 qualifying days" → 6
  const m = s.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
};

/** Estimate payout processing days from frequency text */
const parsePayoutProcessingDays = (freq) => {
  if (!freq) return 7;
  const lower = freq.toLowerCase();
  if (lower.includes("day 1") || lower.includes("next business")) return 1;
  if (lower.includes("daily")) return 2;
  if (lower.includes("twice weekly") || lower.includes("2x")) return 4;
  if (lower.startsWith("weekly") || lower.includes("wednesdays")) return 7;
  if (lower.includes("bi-weekly") || lower.includes("biweekly") || lower.includes("every 5")) return 7;
  if (lower.includes("per cycle")) return 2;
  return 7;
};

/** Resolve 50K profit target from firm data */
const resolveProfitTarget = (firm) => {
  const pt = firm?.profit_targets;
  if (!pt) return 3000;
  for (const key of Object.keys(pt)) {
    const val = pt[key]?.["$50K"];
    if (val) return parseDollarVal(val);
  }
  if (pt["$50K"]) return parseDollarVal(pt["$50K"]);
  return 3000;
};

/** Resolve 50K buffer from pricing array */
const resolveBuffer = (firm) => {
  const pricing = firm?.pricing || [];
  const p50 = pricing.find(p => isPublicEvalType(p.evaluation_type) && (p.size || "").includes("50K"));
  if (p50?.buffer) return parseDollarVal(p50.buffer);
  // Fallback: check path_to_first_payout
  const ptp = firm?.path_to_first_payout;
  if (ptp) {
    const inner = ptp["1-step"] || ptp["starter"] || ptp;
    if (inner?.buffer) return parseDollarVal(inner.buffer);
  }
  return 2000; // conservative default
};

/**
 * Estimate the total days from evaluation start to first payout.
 *
 * @param {Object} firm - Raw firm object from API
 * @returns {{ evalDays: number, fundedDays: number, payoutDays: number, total: number, breakdown: Object }}
 */
export const estimatePathDays = (firm) => {
  if (!firm) return { evalDays: 0, fundedDays: 0, payoutDays: 7, total: 7, breakdown: {} };

  const profitTarget = resolveProfitTarget(firm);
  const consistencyPct = parseConsistencyPct(firm.consistency_rule);
  const minEvalDays = parseInt(firm.min_days) || 0;
  const buffer = resolveBuffer(firm);
  const fundedMinDays = parseFundedMinDays(firm.funded_min_days);
  const payoutDays = parsePayoutProcessingDays(firm.payout_frequency);

  // ── Phase 1: Evaluation ──
  // With consistency rule, max daily profit is capped at consistency% of target
  let evalDays;
  if (consistencyPct > 0 && profitTarget > 0) {
    const maxDailyProfit = profitTarget * consistencyPct;
    evalDays = Math.max(minEvalDays, Math.ceil(profitTarget / maxDailyProfit));
  } else {
    evalDays = Math.max(minEvalDays, 1);
  }

  // ── Phase 2: Funded (buffer build + funded requirements) ──
  let fundedDays;
  if (buffer > 0) {
    // During funded phase, still need to build buffer cushion
    // Use same consistency cap as a conservative daily earnings estimate
    const dailyEst = consistencyPct > 0 ? profitTarget * consistencyPct : 500;
    const bufferBuildDays = Math.ceil(buffer / dailyEst);
    fundedDays = Math.max(fundedMinDays, bufferBuildDays);
  } else {
    fundedDays = fundedMinDays;
  }

  // ── Phase 3: Payout processing ──
  const total = evalDays + fundedDays + payoutDays;

  return {
    evalDays,
    fundedDays,
    payoutDays,
    total,
    breakdown: {
      profitTarget,
      consistencyPct,
      minEvalDays,
      buffer,
      fundedMinDays,
      payoutFreq: firm.payout_frequency,
    },
  };
};

/** Format total estimate as display string */
export const formatEstimate = (total) => {
  if (total <= 1) return "Day 1";
  if (total <= 7) return `~${total} days`;
  return `~${total} days`;
};
